/**
 * Results Tab Component
 * Displays valuation results including growth metrics, financial summary, and insights
 */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Info,
  Percent,
  HelpCircle,
  Loader,
  DollarSign,
  BarChart3,
  Eye
} from 'lucide-react';

const ResultsTab = ({
  valuationResult,
  privateDiscount,
  handleDiscountChange,
  sliderCalculating,
  justRecalculated,
  setShowCalculationModal,
  setShowDiscountInfo,
  navigate,
  onClose
}) => {
  return (
    <div>
      {!valuationResult && (
        <div className="p-8 text-center text-gray-600">
          <p>No valuation results yet. Please click &quot;Calculate Valuation&quot; to see results.</p>
        </div>
      )}

      {valuationResult && (
        <div className="space-y-3">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-green-600 rounded-lg p-4 text-white">
            <h2 className="text-xl font-bold mb-1">Valuation Results</h2>
            <p className="text-emerald-100 text-sm">Advanced multi-year analysis based on your financial data</p>
          </div>

          {/* Main Valuation Card */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
            <div className="p-4">
              {/* Valuation and Metadata Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4 pb-4 border-b border-gray-200">
                {/* Estimated Value */}
                <div>
                  <h3 className="text-gray-600 text-xs font-medium mb-1">Estimated Value</h3>
                  <p className="text-2xl font-bold text-emerald-600">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(valuationResult.valuation_amount)}
                  </p>
                </div>

                {/* Valuation Range */}
                {valuationResult.low_range && valuationResult.high_range && (
                  <div>
                    <h3 className="text-gray-600 text-xs font-medium mb-1">Valuation Range</h3>
                    <p className="text-sm font-semibold text-gray-800">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(valuationResult.low_range)} - {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(valuationResult.high_range)}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">Uncertainty range</p>
                  </div>
                )}

                {/* See Calculation Button */}
                <div className="flex items-end">
                  <button
                    onClick={() => setShowCalculationModal(true)}
                    className="w-full text-xs bg-blue-50 text-blue-700 py-1.5 px-2 rounded hover:bg-blue-100 transition-colors border border-blue-200 flex items-center justify-center gap-1"
                  >
                    <Info size={14} />
                    See Calculation Details
                  </button>
                </div>
              </div>

              {/* Metadata Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                <div>
                  <span className="text-gray-600">Method:</span>
                  <p className="font-semibold text-gray-800 mt-0.5">
                    {valuationResult.selected_method === 'cca' && 'CCA'}
                    {valuationResult.selected_method === 'dcf' && 'DCF'}
                    {valuationResult.selected_method === 'manual' && 'Manual'}
                    {valuationResult.selected_method === 'capitalization' && 'Cap. Earnings'}
                    {valuationResult.selected_method === 'nav' && 'NAV'}
                  </p>
                </div>

                {valuationResult.industry_info && (
                  <div>
                    <span className="text-gray-600">Discount:</span>
                    <p className="font-semibold text-gray-800 mt-0.5">
                      {valuationResult.industry_info.private_discount_applied.toFixed(0)}%
                    </p>
                  </div>
                )}

                {valuationResult.industry_info && (
                  <div className="lg:col-span-1">
                    <span className="text-gray-600">Industry:</span>
                    <p className="font-semibold text-gray-800 mt-0.5 truncate" title={valuationResult.industry_info.name}>
                      {valuationResult.industry_info.name}
                    </p>
                  </div>
                )}

                <div>
                  <span className="text-gray-600">Updated:</span>
                  <p className="font-semibold text-gray-800 mt-0.5">
                    {new Date().toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Private Company Discount Slider */}
          <div className="mb-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-300 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Percent className="text-purple-600" size={18} />
                <label className="text-xs font-semibold text-gray-800">
                  Private Company Discount
                </label>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowDiscountInfo(true);
                  }}
                  className="text-purple-600 hover:text-purple-700"
                >
                  <HelpCircle size={14} />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">
                  {privateDiscount === 0 && "Using public company multiples (no discount)"}
                  {privateDiscount > 0 && privateDiscount < 25 && "Lower discount = Higher valuation"}
                  {privateDiscount === 25 && "Standard private company discount applied"}
                  {privateDiscount > 25 && "Higher discount = More conservative valuation"}
                </span>
                {sliderCalculating && (
                  <Loader className="animate-spin text-purple-600" size={14} />
                )}
                <span className="text-xl font-bold text-purple-600">
                  {privateDiscount}%
                </span>
              </div>
            </div>

            <input
              type="range"
              min="0"
              max="50"
              step="5"
              value={privateDiscount}
              onChange={handleDiscountChange}
              className="w-full h-2 bg-gradient-to-r from-purple-200 to-pink-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              style={{
                background: `linear-gradient(to right, #9333ea 0%, #9333ea ${privateDiscount * 2}%, #e9d5ff ${privateDiscount * 2}%, #e9d5ff 100%)`
              }}
            />

            <div className="flex justify-between text-xs text-gray-600 mt-1.5">
              <span>0% (No discount)</span>
              <span>25% (Standard)</span>
              <span>50% (Maximum)</span>
            </div>

            {valuationResult && (
              <div className="mt-2 pt-2 border-t border-purple-200">
                {sliderCalculating && (
                  <p className="text-xs text-purple-600 flex items-center gap-1">
                    <Loader className="animate-spin" size={11} />
                    Updating valuation...
                  </p>
                )}
                {!sliderCalculating && justRecalculated && (
                  <p className="text-xs text-green-600">
                    ✓ Valuation updated
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Growth Analysis */}
          {valuationResult.growth_metrics && (
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Growth Analysis</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-300 rounded p-2">
                  <p className="text-xs text-blue-800 font-medium mb-0.5">Revenue CAGR</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {valuationResult.growth_metrics.revenue_cagr.toFixed(1)}%
                  </p>
                  <p className="text-xs text-blue-600 mt-0.5">Annual Growth Rate</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-300 rounded p-2">
                  <p className="text-xs text-purple-800 font-medium mb-0.5">EBITDA CAGR</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {valuationResult.growth_metrics.ebitda_cagr.toFixed(1)}%
                  </p>
                  <p className="text-xs text-purple-600 mt-0.5">Profitability Growth</p>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-300 rounded p-2">
                  <p className="text-xs text-amber-800 font-medium mb-0.5">SDE CAGR</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {valuationResult.growth_metrics.sde_cagr.toFixed(1)}%
                  </p>
                  <p className="text-xs text-amber-600 mt-0.5">SDE Growth</p>
                </div>
              </div>
            </div>
          )}

          {/* Financial Summary & Industry Benchmarks Combined */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Financial Summary */}
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Financial Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-300 rounded p-3">
                  <h4 className="font-semibold text-slate-800 mb-2 text-xs flex items-center gap-1">
                    <DollarSign size={14} className="text-slate-600" />
                    Current Year ({new Date().getFullYear()})
                  </h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between items-center py-1 border-b border-slate-200">
                      <span className="text-slate-700">Revenue:</span>
                      <span className="font-bold text-slate-900">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                        }).format(valuationResult.financial_summary.current_year.revenue)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-slate-200">
                      <span className="text-slate-700">EBITDA:</span>
                      <span className="font-bold text-slate-900">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                        }).format(valuationResult.financial_summary.current_year.ebitda)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-slate-200">
                      <span className="text-slate-700">SDE:</span>
                      <span className="font-bold text-slate-900">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                        }).format(valuationResult.financial_summary.current_year.sde)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-slate-700">EBITDA Margin:</span>
                      <span className="font-bold text-emerald-600">
                        {valuationResult.financial_summary.current_year.ebitda_margin.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-300 rounded p-3">
                  <h4 className="font-semibold text-indigo-800 mb-2 text-xs flex items-center gap-1">
                    <BarChart3 size={14} className="text-indigo-600" />
                    Multi-Year Average
                  </h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between items-center py-1 border-b border-indigo-200">
                      <span className="text-indigo-700">Avg Revenue:</span>
                      <span className="font-bold text-indigo-900">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                        }).format(valuationResult.financial_summary.averages.revenue)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-indigo-200">
                      <span className="text-indigo-700">Avg EBITDA:</span>
                      <span className="font-bold text-indigo-900">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                        }).format(valuationResult.financial_summary.averages.ebitda)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-indigo-200">
                      <span className="text-indigo-700">Avg SDE:</span>
                      <span className="font-bold text-indigo-900">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                        }).format(valuationResult.financial_summary.averages.sde)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-indigo-700">Avg EBITDA Margin:</span>
                      <span className="font-bold text-emerald-600">
                        {valuationResult.financial_summary.averages.ebitda_margin.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Industry Benchmarks */}
            {valuationResult.industry_info && (
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3">
                <h3 className="text-sm font-semibold text-gray-800 mb-1">Industry Benchmarks</h3>
                <p className="text-xs text-gray-600 mb-2">
                  {valuationResult.industry_info.name} ({valuationResult.industry_info.business_type === 'private' ? 'Private' : 'Public'} Companies)
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 border border-gray-200 rounded p-2">
                    <p className="text-xs text-gray-700 font-medium mb-1.5">
                      Original ({valuationResult.industry_info.business_type === 'private' ? 'Private' : 'Public'})
                    </p>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">EV/EBITDA:</span>
                        <span className="font-bold text-gray-800">{valuationResult.industry_info.original_multiples.ev_ebitda.toFixed(1)}x</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">EV/Revenue:</span>
                        <span className="font-bold text-gray-800">{valuationResult.industry_info.original_multiples.ev_revenue.toFixed(1)}x</span>
                      </div>
                      {valuationResult.industry_info.ebitda_margin != null && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">EBITDA Margin:</span>
                          <span className="font-bold text-emerald-600">{valuationResult.industry_info.ebitda_margin.toFixed(1)}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-50 to-green-100 border border-emerald-200 rounded p-2">
                    <p className="text-xs text-emerald-800 font-medium mb-1.5">
                      Adjusted ({valuationResult.industry_info.private_discount_applied.toFixed(0)}% discount)
                    </p>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">EV/EBITDA:</span>
                        <span className="font-bold text-emerald-700">{valuationResult.industry_info.adjusted_multiples.ev_ebitda.toFixed(1)}x</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">EV/Revenue:</span>
                        <span className="font-bold text-emerald-700">{valuationResult.industry_info.adjusted_multiples.ev_revenue.toFixed(1)}x</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Key Insights */}
          {valuationResult.insights && valuationResult.insights.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Key Insights</h3>
              <div className="space-y-1.5">
                {valuationResult.insights.map((insight, idx) => (
                  <div
                    key={idx}
                    className={`p-2 rounded border shadow-sm ${insight.type === 'positive'
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'
                        : insight.type === 'warning'
                          ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-300'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 border-red-300'
                      }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-bold">
                        {insight.type === 'positive' ? '✓' : insight.type === 'warning' ? '⚠' : '✗'}
                      </span>
                      <div>
                        <p className="font-semibold text-xs text-gray-800">{insight.category}</p>
                        <p className="text-xs text-gray-700 mt-0.5">{insight.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* View Full Dashboard Button */}
      {valuationResult && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => {
              navigate('/valuation-dashboard');
              onClose();
            }}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg flex items-center gap-2 font-semibold"
          >
            <Eye size={20} />
            View Full Dashboard
          </button>
        </div>
      )}
    </div>
  );
};

ResultsTab.propTypes = {
  valuationResult: PropTypes.object,
  privateDiscount: PropTypes.number.isRequired,
  handleDiscountChange: PropTypes.func.isRequired,
  sliderCalculating: PropTypes.bool.isRequired,
  justRecalculated: PropTypes.bool.isRequired,
  setShowCalculationModal: PropTypes.func.isRequired,
  setShowDiscountInfo: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ResultsTab;
